/**
 * TradeSnapshot — Share-to-chat trade cards.
 *
 * Two components:
 *   - TradeSnapshotCard   — renders inside MessageBubble when message.tradeSnapshot exists
 *   - SharetradePickerModal — lets user pick from their recent closed trades and share
 *
 * Snapshots are server-authoritative — the backend re-fetches the trade from
 * the user's cost-basis history, so PnL can't be forged client-side.
 */

import { useEffect, useState } from 'react';
import { X, TrendingUp, TrendingDown, Loader2, AlertTriangle, Send, Clock } from 'lucide-react';
import { getRecentClosedTrades, shareTradeSnapshot } from '../services/api';

// ─── Snapshot Card (inline in chat message) ───────────────────────────────

export function TradeSnapshotCard({ snapshot }) {
  if (!snapshot) return null;
  const isWin = (snapshot.pnl || 0) >= 0;
  const colorMap = {
    emerald: 'border-emerald-500/30 from-emerald-500/5 to-emerald-500/10',
    cyan:    'border-cyan-500/30 from-cyan-500/5 to-cyan-500/10',
    amber:   'border-amber-500/30 from-amber-500/5 to-amber-500/10',
    violet:  'border-violet-500/30 from-violet-500/5 to-violet-500/10',
    blue:    'border-blue-500/30 from-blue-500/5 to-blue-500/10',
    purple:  'border-purple-500/30 from-purple-500/5 to-purple-500/10',
    pink:    'border-pink-500/30 from-pink-500/5 to-pink-500/10',
    gray:    'border-gray-500/30 from-gray-500/5 to-gray-500/10',
  };
  const accent = colorMap[snapshot.sourceColor] || colorMap.gray;
  const pnlColor = isWin ? 'text-emerald-400' : 'text-red-400';

  return (
    <div className={`mt-2 rounded-xl border bg-gradient-to-br ${accent} p-3 max-w-md`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-base">{snapshot.sourceIcon}</span>
          <div>
            <p className="text-xs font-bold text-white">{snapshot.sourceLabel}</p>
            <p className="text-[9px] text-gray-500 uppercase tracking-wider">{snapshot.asset}</p>
          </div>
        </div>
        <div className={`text-right ${pnlColor}`}>
          <p className="text-lg font-bold flex items-center gap-1 justify-end">
            {isWin ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {isWin ? '+' : ''}{snapshot.pnlPct != null ? `${snapshot.pnlPct.toFixed(2)}%` : '—'}
          </p>
          <p className="text-[10px] font-mono">
            {isWin ? '+' : ''}${Math.abs(snapshot.pnl).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Entry → Exit prices */}
      <div className="bg-dark-800/50 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
        <div>
          <p className="text-[9px] text-gray-500 uppercase">Entry</p>
          <p className="text-white font-mono">${formatPrice(snapshot.entryPrice)}</p>
        </div>
        <div className="text-gray-600">→</div>
        <div className="text-right">
          <p className="text-[9px] text-gray-500 uppercase">Exit</p>
          <p className="text-white font-mono">${formatPrice(snapshot.exitPrice)}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500">
        <span>{snapshot.qty} {snapshot.asset}</span>
        {snapshot.heldMs && (
          <span className="flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" /> {formatDuration(snapshot.heldMs)}
          </span>
        )}
        <span className="ml-auto text-[9px] text-gray-600 italic">verified by haythix</span>
      </div>
    </div>
  );
}

// ─── Picker Modal (user shares one of their trades) ────────────────────────

export function ShareTradePickerModal({ roomId, onClose, onShared }) {
  const [trades, setTrades] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getRecentClosedTrades(20)
      .then(d => { if (!cancelled) setTrades(d?.trades || []); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function share() {
    if (!selected) return;
    setSubmitting(true);
    try {
      await shareTradeSnapshot(roomId, selected.snapshotId, comment);
      if (onShared) onShared();
      onClose();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-white">Share a Trade</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">Pick one of your closed trades — server verifies the PnL.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {loading && (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-accent-primary animate-spin" /></div>
        )}

        {error && !loading && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 text-xs text-red-400 flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {!loading && trades && trades.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-400 mb-1">No closed trades yet</p>
            <p className="text-[11px] text-gray-600">Once you close a trade — manual, Smart Trade, Grid Bot, DCA, or Algo Order — it'll show up here for sharing.</p>
          </div>
        )}

        {!loading && trades && trades.length > 0 && !selected && (
          <div className="space-y-2">
            {trades.map(t => (
              <button key={t.snapshotId} onClick={() => setSelected(t)}
                className="w-full bg-dark-700/30 hover:bg-dark-700/60 border border-dark-700/50 hover:border-accent-primary/40 rounded-xl px-3 py-2.5 text-left transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className="text-base flex-shrink-0">{t.sourceIcon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{t.asset} <span className="text-gray-500 text-xs">· {t.sourceLabel}</span></p>
                      <p className="text-[10px] text-gray-500 truncate">
                        ${formatPrice(t.entryPrice)} → ${formatPrice(t.exitPrice)} · {new Date(t.closedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className={`text-right flex-shrink-0 ml-2 ${(t.pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    <p className="text-sm font-bold">{(t.pnl || 0) >= 0 ? '+' : ''}{t.pnlPct != null ? `${t.pnlPct.toFixed(2)}%` : '—'}</p>
                    <p className="text-[9px]">{(t.pnl || 0) >= 0 ? '+' : ''}${Math.abs(t.pnl).toFixed(2)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Preview:</p>
            <TradeSnapshotCard snapshot={selected} />

            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block">Add a comment (optional)</label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} maxLength={200} rows={2}
                placeholder="e.g., Loved this setup — perfect 4h breakout entry"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none resize-none" />
              <p className="text-[9px] text-gray-600 text-right mt-1">{comment.length}/200</p>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-xl text-sm text-gray-400 border border-dark-600">
                ← Back
              </button>
              <button onClick={share} disabled={submitting}
                className="flex-1 py-2.5 rounded-xl bg-accent-primary text-dark-900 font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {submitting ? 'Sharing…' : 'Share to chat'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatPrice(p) {
  if (p == null || !isFinite(p)) return '—';
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (p >= 1) return p.toLocaleString(undefined, { maximumFractionDigits: 4 });
  if (p >= 0.01) return p.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return p.toExponential(2);
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}
